/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table as UITable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TableLoad } from "@/components/ui/table-load";
import { EmptyTable } from "@/components/Datatable/components/EmptyTable";
import { ExtendedColumnDef } from "components/Datatable";
import { Meta } from "src/@types/paginated";
import { IPayment, IStoreColumns } from "src/@types/stores";
import { getPagesToShow } from "@/utils/pagination";
import { useFilterActions } from "../../../ducks/filter/hooks/actions";
import { useFilterState } from "../../../ducks/filter/hooks/filterState";

interface ITableProps<T> {
  data: IPayment[];
  meta: Meta;
  columns: ExtendedColumnDef<T>[];
  isFetching?: boolean;
}

export default function Table({
  data,
  meta,
  columns,
  isFetching = false,
}: ITableProps<IStoreColumns>) {
  const { updateFilter } = useFilterActions();
  const filterState = useFilterState();
  const { filters } = filterState || { filters: {} };

  const table = useReactTable({
    data: data as any,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const selectedPage = (page: number | string) => {
    if (page === "...") return "ignore";
    if (!meta.currentPage && page === 1) return "selected";
    return page === meta.currentPage ? "selected" : "";
  };

  return (
    <div>
      <div className="rounded-t-md border">
        <UITable>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isFetching ? (
              <TableLoad colSpan={columns.length} />
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="hover:bg-gray-100"
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <EmptyTable text="Selecione uma data de conciliação para ver os dados." />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </UITable>
      </div>

      {/* Paginação — mantida exatamente como estava */}
      <div className="flex w-full items-center justify-between pagination-container">
        <div className="flex text-sm">
          <button
            className={`cursor-pointer px-3 py-1 bg-[#2b0c1a] text-white rounded-l-lg rounded-tl-none ${
              meta.prev && !isFetching ? "" : "text-[#8d91a2] bg-[#d9dae0] cursor-not-allowed"
            }`}
            onClick={() =>
              updateFilter({
                key: "meta",
                value: { page: (filters.meta?.page || 1) - 1 || 1 },
              })
            }
            disabled={!meta.prev || isFetching}
          >
            Anterior
          </button>
          {getPagesToShow(meta).map((page: any) => (
            <span
              key={page}
              className={`flex justify-center px-2 py-2 border border-[#dcdce3] border-l-0 border-t-0 min-w-[29px] ${
                selectedPage(page) === "selected" ? "bg-[#f4c83e]" : ""
              } ${page !== "..." ? "cursor-pointer hover:underline" : ""}`}
              onClick={() => {
                if (typeof page === "number") {
                  updateFilter({
                    key: "meta",
                    value: { page: page },
                  });
                }
              }}
            >
              {page}
            </span>
          ))}
          <button
            className={`cursor-pointer px-3 py-1 bg-[#2b0c1a] text-white rounded-r-lg rounded-tr-none ${
              meta.next && !isFetching ? "" : "text-[#8d91a2] bg-[#d9dae0] cursor-not-allowed"
            }`}
            onClick={() =>
              updateFilter({
                key: "meta",
                value: { page: (filters.meta?.page || 1) + 1 || 1 },
              })
            }
            disabled={!meta.next || isFetching}
          >
            Próximo
          </button>
        </div>
        <span className="text-sm font-light leading-4 text-[#444444]">{`${meta.currentPage ?? 0} de ${
          meta.lastPage ?? 0
        } página${meta?.total !== 1 ? "s" : ""}`}</span>
      </div>
    </div>
  );
}
